import {useEffect, useMemo, useState} from 'react';
import {ColumnDef} from '@tanstack/react-table';
import {Button as BootstrapButton, Card, Form, OverlayTrigger, Tooltip} from 'react-bootstrap';
import {IntlShape, useIntl} from 'react-intl';
import axios from 'axios';
import TableMetronic from '../../../../../app/common/components/table/TableMetronic';
import Badge from '../../../../../app/common/components/Badge';
import Level3Title from '../../../../../app/common/components/title/Level3Title';
import Button from '../../../../../Components/Button';
import FilterSelect, {FilterOption} from '../../../../../app/common/components/select/FilterSelect';
import {
  ApiAuthorizationOverride,
  ApiAuthorizationOverrides,
  ApiLimitation,
  ApiMoneyAmount,
} from '../../../Api/telephonie';
import {
  useGetAuthorizationOverrides,
  useGetLimitation,
  useGetTrunkNumeros,
  useUpdateAuthorizationOverrides,
  useUpdateLimitation,
} from '../../../Hooks/TelephonieManager';
import Loading from '../../../../../app/common/components/Loading';

/**
 * « Restriction d'appels » (Voice/Limitation.aspx) : restriction sortante par catégorie de
 * destination — « Tous les numéros » lit et modifie la limitation du service, un numéro
 * choisi ses propres overrides, quand la prestation a la limitation avancée. Les catégories
 * et leurs libellés viennent du référentiel pays de l'API (api-telephonie), pas d'une liste
 * fixe ici : deux services de pays différents n'exposent pas forcément les mêmes.
 */

const TOUS_LES_NUMEROS = 'Tous les numéros';

interface NumeroSelectorProps {
  numeroOptions: FilterOption<string>[];
  numero: string;
  setNumero: (numero: string) => void;
}

/** L'API relaie les erreurs de la plateforme voix en RFC 9457 (`detail`, `status`). */
interface ApiProblem {
  detail?: string;
}

const messageErreurLimitation = (error: unknown, intl: IntlShape): string => {
  if (axios.isAxiosError(error) && error.response) {
    if (error.response.status === 409) {
      return intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.ERROR_LOCKED'});
    }
    const detail = (error.response.data as ApiProblem | undefined)?.detail;
    if (error.response.status === 422 && detail) {
      return detail;
    }
  }
  return intl.formatMessage({id: 'COMMON.INTERNAL_ERROR'});
};

const useNumeroOptions = (
  serviceId: number
): {numeroOptions: FilterOption<string>[]; isLoading: boolean} => {
  const intl = useIntl();
  const {data, isLoading} = useGetTrunkNumeros(serviceId);
  const numeroOptions: FilterOption<string>[] = [
    {
      value: TOUS_LES_NUMEROS,
      label: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.TOUS_LES_NUMEROS'}),
    },
    ...(data?.numeros ?? []).map((numero) => ({
      value: String(numero.numeroId),
      label: numero.numero,
    })),
  ];
  return {numeroOptions, isLoading};
};

const SortantsEntete = (): JSX.Element => {
  const intl = useIntl();
  return (
    <>
      <div className="d-flex align-items-center gap-2 mb-4">
        <Level3Title title={intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.SORTANTS_TITLE'})} />
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip>
              {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.SORTANTS_DESCRIPTION'})}
            </Tooltip>
          }
        >
          <i className="bi bi-question-circle-fill text-primary fs-4 cursor-pointer" />
        </OverlayTrigger>
      </div>
      <p className="text-muted fs-6 mb-5">
        {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.SORTANTS_DESCRIPTION'})}
      </p>
    </>
  );
};

/**
 * Suit le formatage monétaire déjà utilisé ailleurs dans le repo
 * (`app/common/tools/amount.ts`), paramétré par la devise réelle du montant :
 * `financialLimits.inaLimit` porte la sienne, pas nécessairement CHF.
 */
const formatMoneyAmount = (money: ApiMoneyAmount, locale: string): string =>
  parseFloat(money.amount).toLocaleString(locale, {
    style: 'currency',
    currency: money.currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/**
 * Budget mensuel des numéros à valeur ajoutée (`financialLimits.inaLimit`), affiché quel que
 * soit le numéro sélectionné : c'est un budget de service, pas un réglage par numéro.
 */
const BudgetIna = ({limitation}: {limitation: ApiLimitation}): JSX.Element | null => {
  const intl = useIntl();
  const {inaLimit} = limitation.financialLimits;
  const {inaUsedPercent} = limitation.usage;

  if (!inaLimit) {
    return null;
  }

  const limiteAtteinte = (inaUsedPercent ?? 0) >= 100;

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center mb-4"
        style={{maxWidth: 500}}
      >
        <span className="fs-6">
          {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.SORTANTS.LIMITATION_09XX'})}
        </span>
        <span className="fw-bold fs-6">
          {intl.formatMessage(
            {id: 'TELEPHONIE.RESTRICTION.SORTANTS.BUDGET_MONTANT'},
            {budget: formatMoneyAmount(inaLimit, intl.locale)}
          )}
        </span>
      </div>
      {limiteAtteinte && (
        <div className="text-danger fw-bold fs-6 mb-4">
          <i className="bi bi-exclamation-triangle-fill me-2" />
          {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.SORTANTS.LIMITE_ATTEINTE'})}
        </div>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Appels sortants — limitation du service (« Tous les numéros »)
// ---------------------------------------------------------------------------

interface AppelSortantRow {
  type: string;
  appelsVers: string;
  indicatifs?: string;
  autorise: boolean;
}

const lignesDepuisLimitation = (limitation: ApiLimitation): AppelSortantRow[] =>
  limitation.authorizations.map((authorization) => ({
    type: authorization.type,
    appelsVers: authorization.label,
    indicatifs: authorization.description,
    autorise: authorization.allowed,
  }));

interface ServiceViewProps extends NumeroSelectorProps {
  serviceId: number;
  onConfirm: () => void;
}

const LimitationServiceView = ({
  serviceId,
  numeroOptions,
  numero,
  setNumero,
  onConfirm,
}: ServiceViewProps): JSX.Element | null => {
  const intl = useIntl();
  const {data, isLoading, isError} = useGetLimitation(serviceId);
  const updateLimitation = useUpdateLimitation(serviceId);
  const [vue, setVue] = useState<'liste' | 'modifier'>('liste');
  const [lignes, setLignes] = useState<AppelSortantRow[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setLignes(lignesDepuisLimitation(data));
    }
  }, [data]);

  const basculerAutorisation = (type: string, autorise: boolean): void => {
    setLignes((precedent) =>
      precedent.map((ligne) => (ligne.type === type ? {...ligne, autorise} : ligne))
    );
  };

  const confirmer = (): void => {
    if (!data) {
      return;
    }
    setErreur(null);
    updateLimitation.mutate(
      {
        ...data,
        authorizations: lignes.map(({type, appelsVers, autorise}) => ({
          type,
          label: appelsVers,
          allowed: autorise,
        })),
      },
      {
        onSuccess: () => {
          setVue('liste');
          onConfirm();
        },
        onError: (error) => setErreur(messageErreurLimitation(error, intl)),
      }
    );
  };

  const annuler = (): void => {
    if (data) {
      setLignes(lignesDepuisLimitation(data));
    }
    setErreur(null);
    setVue('liste');
  };

  const columnsConsultation = useMemo<ColumnDef<AppelSortantRow>[]>(
    () => [
      {
        header: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.COL_APPELS_VERS'}),
        accessorKey: 'appelsVers',
        size: 320,
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.COL_INDICATIFS'}),
        accessorKey: 'indicatifs',
        size: 320,
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.COL_DROIT'}),
        accessorKey: 'autorise',
        size: 160,
        cell: (info) => (
          <Badge
            text={
              info.row.original.autorise
                ? intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.AUTORISE'})
                : intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.INTERDIT'})
            }
            color={info.row.original.autorise ? 'success' : 'danger'}
            light
          />
        ),
      },
    ],
    [intl]
  );

  const columnsModifier = useMemo<ColumnDef<AppelSortantRow>[]>(
    () => [
      {
        header: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.COL_APPELS_VERS'}),
        accessorKey: 'appelsVers',
        size: 320,
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.COL_INDICATIFS'}),
        accessorKey: 'indicatifs',
        size: 320,
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.COL_DROIT'}),
        accessorKey: 'autorise',
        size: 220,
        cell: (info) => (
          <div className="d-flex gap-4">
            <Form.Check
              type="radio"
              name={`droit-${info.row.original.type}`}
              id={`droit-${info.row.original.type}-autorise`}
              label={intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.AUTORISE'})}
              checked={info.row.original.autorise}
              onChange={() => basculerAutorisation(info.row.original.type, true)}
            />
            <Form.Check
              type="radio"
              name={`droit-${info.row.original.type}`}
              id={`droit-${info.row.original.type}-interdit`}
              label={intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.INTERDIT'})}
              checked={!info.row.original.autorise}
              onChange={() => basculerAutorisation(info.row.original.type, false)}
            />
          </div>
        ),
      },
    ],
    [intl]
  );

  if (isError) {
    return <p>{intl.formatMessage({id: 'COMMON.INTERNAL_ERROR'})}</p>;
  }

  if (isLoading || !data) {
    return <Loading className="col-md-6" />;
  }

  if (vue === 'modifier') {
    return (
      <>
        {data.lockedByProductLine && (
          <p className="text-muted fs-7 mb-3">
            {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.LOCKED_HELP'})}
          </p>
        )}
        {erreur && <div className="fw-bold text-danger mb-3">{erreur}</div>}
        <TableMetronic<AppelSortantRow>
          data={lignes}
          columns={columnsModifier}
          searchBar={false}
          stackedHeader
          toolbar={<FilterSelect options={numeroOptions} value={numero} onChange={setNumero} />}
        />
        <div className="text-center mt-5 d-flex justify-content-center gap-3">
          <Button
            label={intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.CONFIRMER'})}
            disabled={data.lockedByProductLine || updateLimitation.isLoading}
            onClick={confirmer}
          />
          <BootstrapButton variant="secondary" size="sm" onClick={annuler}>
            {intl.formatMessage({id: 'CANCEL'})}
          </BootstrapButton>
        </div>
      </>
    );
  }

  return (
    <>
      {data.lockedByProductLine && (
        <p className="text-muted fs-7 mb-3">
          {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.LOCKED_HELP'})}
        </p>
      )}
      <TableMetronic<AppelSortantRow>
        data={lignes}
        columns={columnsConsultation}
        searchBar={false}
        stackedHeader
        toolbar={<FilterSelect options={numeroOptions} value={numero} onChange={setNumero} />}
      />
      <div className="text-center mt-5">
        <Button
          label={intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.MODIFIER'})}
          faIcon="bi bi-pencil fs-6 me-2"
          disabled={data.lockedByProductLine}
          onClick={() => setVue('modifier')}
        />
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Appels sortants — overrides d'un numéro
// ---------------------------------------------------------------------------

interface OverrideRow {
  type: string;
  appelsVers: string;
  indicatifs?: string;
  override: ApiAuthorizationOverride['override'];
}

const lignesDepuisOverrides = (overrides: ApiAuthorizationOverrides): OverrideRow[] =>
  overrides.authorizations.map((authorization) => ({
    type: authorization.type,
    appelsVers: authorization.label,
    indicatifs: authorization.description,
    override: authorization.override,
  }));

const badgeOverride = (
  override: ApiAuthorizationOverride['override'],
  intl: IntlShape
): {text: string; color: string} => {
  if (override === 'allow') {
    return {text: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.AUTORISE'}), color: 'success'};
  }
  if (override === 'deny') {
    return {text: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.INTERDIT'}), color: 'danger'};
  }
  return {text: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.HERITE'}), color: 'secondary'};
};

interface NumberViewProps extends NumeroSelectorProps {
  serviceId: number;
  numberId: number;
  onConfirm: () => void;
}

const AuthorizationOverridesView = ({
  serviceId,
  numberId,
  numeroOptions,
  numero,
  setNumero,
  onConfirm,
}: NumberViewProps): JSX.Element | null => {
  const intl = useIntl();
  const {
    data: limitation,
    isLoading: limitationLoading,
    isError: limitationError,
  } = useGetLimitation(serviceId);
  const {data, isLoading, isError} = useGetAuthorizationOverrides(serviceId, numberId);
  const updateOverrides = useUpdateAuthorizationOverrides(serviceId, numberId);
  const [vue, setVue] = useState<'liste' | 'modifier'>('liste');
  const [lignes, setLignes] = useState<OverrideRow[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setLignes(lignesDepuisOverrides(data));
    }
  }, [data]);

  const basculerOverride = (type: string, override: ApiAuthorizationOverride['override']): void => {
    setLignes((precedent) =>
      precedent.map((ligne) => (ligne.type === type ? {...ligne, override} : ligne))
    );
  };

  const confirmer = (): void => {
    if (!data) {
      return;
    }
    setErreur(null);
    updateOverrides.mutate(
      {
        ...data,
        authorizations: lignes.map(({type, appelsVers, override}) => ({
          type,
          label: appelsVers,
          override,
        })),
      },
      {
        onSuccess: () => {
          setVue('liste');
          onConfirm();
        },
        onError: (error) => setErreur(messageErreurLimitation(error, intl)),
      }
    );
  };

  const annuler = (): void => {
    if (data) {
      setLignes(lignesDepuisOverrides(data));
    }
    setErreur(null);
    setVue('liste');
  };

  const columnsConsultation = useMemo<ColumnDef<OverrideRow>[]>(
    () => [
      {
        header: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.COL_APPELS_VERS'}),
        accessorKey: 'appelsVers',
        size: 320,
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.COL_INDICATIFS'}),
        accessorKey: 'indicatifs',
        size: 320,
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.COL_DROIT'}),
        accessorKey: 'override',
        size: 160,
        cell: (info) => {
          const badge = badgeOverride(info.row.original.override, intl);
          return <Badge text={badge.text} color={badge.color} light />;
        },
      },
    ],
    [intl]
  );

  const columnsModifier = useMemo<ColumnDef<OverrideRow>[]>(
    () => [
      {
        header: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.COL_APPELS_VERS'}),
        accessorKey: 'appelsVers',
        size: 320,
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.COL_INDICATIFS'}),
        accessorKey: 'indicatifs',
        size: 320,
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.COL_DROIT'}),
        accessorKey: 'override',
        size: 320,
        cell: (info) => (
          <div className="d-flex gap-4">
            <Form.Check
              type="radio"
              name={`override-${info.row.original.type}`}
              id={`override-${info.row.original.type}-herite`}
              label={intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.HERITE'})}
              checked={info.row.original.override === 'inherit'}
              onChange={() => basculerOverride(info.row.original.type, 'inherit')}
            />
            <Form.Check
              type="radio"
              name={`override-${info.row.original.type}`}
              id={`override-${info.row.original.type}-autorise`}
              label={intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.AUTORISE'})}
              checked={info.row.original.override === 'allow'}
              onChange={() => basculerOverride(info.row.original.type, 'allow')}
            />
            <Form.Check
              type="radio"
              name={`override-${info.row.original.type}`}
              id={`override-${info.row.original.type}-interdit`}
              label={intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.INTERDIT'})}
              checked={info.row.original.override === 'deny'}
              onChange={() => basculerOverride(info.row.original.type, 'deny')}
            />
          </div>
        ),
      },
    ],
    [intl]
  );

  if (isError || limitationError) {
    return <p>{intl.formatMessage({id: 'COMMON.INTERNAL_ERROR'})}</p>;
  }

  if (isLoading || limitationLoading || !data || !limitation) {
    return <Loading className="col-md-6" />;
  }

  if (vue === 'modifier') {
    return (
      <>
        {limitation.lockedByProductLine && (
          <p className="text-muted fs-7 mb-3">
            {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.LOCKED_HELP'})}
          </p>
        )}
        {erreur && <div className="fw-bold text-danger mb-3">{erreur}</div>}
        <TableMetronic<OverrideRow>
          data={lignes}
          columns={columnsModifier}
          searchBar={false}
          stackedHeader
          toolbar={<FilterSelect options={numeroOptions} value={numero} onChange={setNumero} />}
        />
        <div className="text-center mt-5 d-flex justify-content-center gap-3">
          <Button
            label={intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.CONFIRMER'})}
            disabled={limitation.lockedByProductLine || updateOverrides.isLoading}
            onClick={confirmer}
          />
          <BootstrapButton variant="secondary" size="sm" onClick={annuler}>
            {intl.formatMessage({id: 'CANCEL'})}
          </BootstrapButton>
        </div>
      </>
    );
  }

  return (
    <>
      {limitation.lockedByProductLine && (
        <p className="text-muted fs-7 mb-3">
          {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.LOCKED_HELP'})}
        </p>
      )}
      <TableMetronic<OverrideRow>
        data={lignes}
        columns={columnsConsultation}
        searchBar={false}
        stackedHeader
        toolbar={<FilterSelect options={numeroOptions} value={numero} onChange={setNumero} />}
      />
      <div className="text-center mt-5">
        <Button
          label={intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.MODIFIER'})}
          faIcon="bi bi-pencil fs-6 me-2"
          disabled={limitation.lockedByProductLine}
          onClick={() => setVue('modifier')}
        />
      </div>
    </>
  );
};

const RestrictionAppelsSortants = ({
  serviceId,
  onConfirm,
  numeroInitial,
}: {
  serviceId: number;
  onConfirm: () => void;
  /** `numeroId` (en string) du numéro à présélectionner — sinon « Tous les numéros ». */
  numeroInitial?: string;
}): JSX.Element | null => {
  const [numero, setNumero] = useState<string>(numeroInitial ?? TOUS_LES_NUMEROS);
  const {numeroOptions} = useNumeroOptions(serviceId);
  const {data: limitation} = useGetLimitation(serviceId);

  return (
    <div className="mt-4">
      <Card>
        <Card.Body>
          <SortantsEntete />
          {limitation && <BudgetIna limitation={limitation} />}
          {numero === TOUS_LES_NUMEROS ? (
            <LimitationServiceView
              serviceId={serviceId}
              numeroOptions={numeroOptions}
              numero={numero}
              setNumero={setNumero}
              onConfirm={onConfirm}
            />
          ) : (
            <AuthorizationOverridesView
              key={numero}
              serviceId={serviceId}
              numberId={Number(numero)}
              numeroOptions={numeroOptions}
              numero={numero}
              setNumero={setNumero}
              onConfirm={onConfirm}
            />
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default RestrictionAppelsSortants;
